const fs = require('fs');
const tls = require('tls');
const SolclientSessionLib = require('solclient-session');
const { ErrorSubcode, OperationError } = require('solclient-error');
const { LOG_TRACE, LOG_DEBUG, LOG_WARN } = require('solclient-log');
const { parseURL } = require('solclient-util');

/**
 * Node TLS socket option generation factored out from WS transport, shared with Tcp transport.
 * Internal mixin class, not intended to be publicly visible.
 * @private
 */

class NodeTLSOptsMixin {
  /**
   * Overwrite Node.js default hostname check callback by verifying cnNameList against subject CN
   * in the server certificate. If cnNameList is a empty string, it means all CNs are acceptable.
   * @param {String} host The hostname for checking
   * @param {String} cert The server certificate for checking
   * @name solace.WebTransportSession#checkCertificateCNs
   * @returns {?Error} Any error generated by the certificate check
   * @private
   */
  checkCertificateCNs(host, cert) {
    if (this._props.sslTrustedCommonNameList && this._props.sslTrustedCommonNameList.length > 0) {
      // no Closure Compiler externs for certificates
      LOG_DEBUG('server cert:', cert);
      const subject = cert['subject']; // eslint-disable-line dot-notation
      let valid = false;
      if (subject) {
        const cn = subject['CN']; // eslint-disable-line dot-notation
        valid = this._props.sslTrustedCommonNameList.some(name =>
          name.toLowerCase() === cn.toLowerCase()
        );
      }
      if (!valid) {
        const error = new Error('Server certificate not trusted - no common name match');
        error.reason = 'Server certificate not trusted - no common name match';
        error.host = host;
        error.cert = cert;
        return error;
      }
    }
    return undefined;
  }

  /**
   * Create the TLS options object used in wss:// and tcps:// connection establishment.
   * The same options are needed by the ws and tls socket constructors.
   * @returns {Object} WebSocket/TLSSocket options object
   * @private
   */
  createTLSOptions() {
    /* eslint-disable dot-notation */
    const props = this._props;
    const options = {};
    const useClientCert = (
      props.authenticationScheme === SolclientSessionLib.AuthenticationScheme.CLIENT_CERTIFICATE
    );

    // available tls options:
    //  pfx, key, passphrase, cert, ca, ciphers, rejectUnauthorized,
    //  secureProtocol, secureOptions, checkServerIdentity

    const constMap = SolclientSessionLib.SessionProperties.SslProtocolExcludeConstantMap;
    options['secureOptions'] = constMap['sslv2'] | constMap['sslv3'];
    (props.sslExcludedProtocols || []).forEach((protocol) => {
      options['secureOptions'] |= constMap[protocol.toLowerCase()];
    });

    // TLS min/maxVersion are case sensitive.
    const supported = SolclientSessionLib.SessionProperties.SUPPORTED_SSL_PROTOCOLS;
    const supportedLC = supported.map(p => p.toLowerCase());
    if (props.tlsMinProtocol) {
      const minIndex = supportedLC.indexOf(props.tlsMinProtocol.toLowerCase())
      options['minVersion'] = supported[minIndex];
    } else {
      // Yes, you can lock yourself out by setting max to 1.1 and leaving min at default. 
      // It's also the nodeJS default as of 2025, but we want to cement it in for ourselves for now.
      options['minVersion'] = 'TLSv1.2'
    }

    if (props.tlsMaxProtocol) {
      const maxIndex = supportedLC.indexOf(props.tlsMaxProtocol.toLowerCase())
      options['maxVersion'] = supported[maxIndex];
    }

    if (props.sslCipherSuites) {
      // This is a bit weird but actually makes sense:
      // nodeJS has one cipher list for pre- and post-TLSv1.3.
      // The TLSv1.3 ones, which we don't want to make changeable via session props, start with "TLS_" .
      // nodeJS also has platform level defaults and configs for ciphers, which we do want to respect for TLSv1.3 .
      // So if the user suppliess a pre-TLSv1.3 cipher list, we add the TLSv1.3 ones from DEFAULT_CIPHERS to it.

      const default_ciphers = tls.DEFAULT_CIPHERS.split(':').map(c => c.trim());
      const default_tls13_ciphers = default_ciphers.filter(c => c.startsWith("TLS_"))

      const ciphers = props.sslCipherSuites.split(',');
      const trimmed = ciphers.map(cipher => cipher.trim());
      options['ciphers'] = default_tls13_ciphers.concat(trimmed).join(':');
    }

    options['rejectUnauthorized'] = props.sslValidateCertificate;

    // only set checkServerIdentity callback if rejectUnauthorized
    if (options['rejectUnauthorized'] && Array.isArray(props.sslTrustedCommonNameList)) {
      options['checkServerIdentity'] = this.checkCertificateCNs.bind(this);
    }

    // only set trust stores and and checkServerIdentity callback
    // if rejectUnauthorized is true or authClientCert
    if ((options['rejectUnauthorized'] || useClientCert) &&
      props.sslTrustStores && props.sslTrustStores.length) {
      const tsContents = [];
      props.sslTrustStores.forEach((tsFile) => {
        try {
          tsContents.push(fs.readFileSync(tsFile));
        } catch (e) {
          LOG_WARN(`Failed to load trust store ${tsFile}`, e.message);
          LOG_TRACE('Error details:', e.stack || e);
          throw new OperationError(`Failed to load trust store ${tsFile}`,
            ErrorSubcode.FAILED_LOADING_TRUSTSTORE,
            e);
        }
      });
      options['ca'] = tsContents;
    }


    if (useClientCert) {
      if (props.sslPfx) {
        try {
          options['pfx'] = fs.readFileSync(props.sslPfx);
          options['passphrase'] = props.sslPfxPassword;
        } catch (e) {
          LOG_WARN(`Failed to load pfx file ${props.sslPfx}`, e.message);
          LOG_TRACE('Error details:', e.stack || e);
          throw new OperationError(`Failed to load pfx ${props.sslPfx}`,
            ErrorSubcode.FAILED_LOADING_CERTIFICATE_AND_KEY, e);
        }
      }
      if (props.sslPrivateKey) {
        try {
          options['key'] = fs.readFileSync(props.sslPrivateKey);
          options['passphrase'] = props.sslPrivateKeyPassword;
        } catch (e) {
          LOG_WARN(`Failed to load private key ${props.sslPrivateKey}`, e.message);
          LOG_TRACE('Error details:', e.stack || e);
          throw new OperationError(`Failed to load private key ${props.sslPrivateKey}`,
            ErrorSubcode.FAILED_LOADING_CERTIFICATE_AND_KEY, e);
        }
      }
      if (props.sslCertificate) {
        try {
          options['cert'] = fs.readFileSync(props.sslCertificate);
        } catch (e) {
          LOG_WARN(`Failed to load certificate file ${props.sslCertificate}`, e.message);
          LOG_TRACE('Error details:', e.stack || e);
          throw new OperationError(`Failed to load certificate ${props.sslCertificate}`,
            ErrorSubcode.FAILED_LOADING_CERTIFICATE_AND_KEY, e);
        }
      }
    }
    //SNI
    const hostname = parseURL(this._url).hostname;
    options['servername'] = hostname;
    return options;
    /* eslint-enable dot-notation */
  }

}

module.exports.NodeTLSOptsMixin = NodeTLSOptsMixin;
